import { useState, useRef, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PencilIcon } from "@/components/developer/DashboardIcons";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import YearsExperiencePopover from "./skills/YearsExperiencePopover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";

interface Skill {
  id: string;
  skill_name: string;
  years_of_experience: number;
  category_id: string;
}

interface SkillCategory {
  id: string;
  category_name: string;
}

interface SkillTerm {
  id: string;
  term: string;
  category: string | null;
}

interface TempSkill {
  id?: string;
  skill_name: string;
  years_of_experience: number;
  category_id: string;
  isNew?: boolean;
}

const TechnicalSkills = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [newSkill, setNewSkill] = useState("");
  const [newYears, setNewYears] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [tempSkills, setTempSkills] = useState<TempSkill[]>([]);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const { data: categories = [], isFetching: isCategoriesLoading } = useQuery({
    queryKey: ["skillCategories"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("skill_categories")
        .select("*")
        .order("category_name");

      if (error) throw error;
      return data as SkillCategory[];
    },
  });

  const { data: skills = [], isFetching: isSkillsLoading } = useQuery({
    queryKey: ["skills"],
    queryFn: async () => {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error('No user found');

      const { data, error } = await supabase
        .from("skills")
        .select("*")
        .eq('user_id', user.user.id)
        .order("years_of_experience", { ascending: false });

      if (error) throw error;
      return data as Skill[];
    },
  });

  const { data: skillTerms = [], isFetching: isTermsLoading } = useQuery({
    queryKey: ["skillTerms"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("skill_terms")
        .select("*")
        .order("term");

      if (error) throw error;
      return data as SkillTerm[];
    },
  });

  const handleStartEditing = () => {
    setTempSkills(skills.map(skill => ({
      id: skill.id,
      skill_name: skill.skill_name,
      years_of_experience: skill.years_of_experience,
      category_id: skill.category_id
    })));
    setIsEditing(true);
  };

  const handleSave = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("No user found");

      let categoryId = categories[0]?.id;

      if (!categoryId) {
        const { data: newCategory, error: categoryError } = await supabase
          .from("skill_categories")
          .insert({
            category_name: "General",
            user_id: user.id
          })
          .select()
          .single();

        if (categoryError) throw categoryError;
        categoryId = newCategory.id;
      }

      // Find skills to delete
      const skillsToDelete = skills.filter(skill =>
        !tempSkills.some(tempSkill => tempSkill.id === skill.id)
      );

      // Find skills to add (new skills)
      const skillsToAdd = tempSkills.filter(skill => !skill.id);

      // Find skills to update (changed years of experience)
      const skillsToUpdate = tempSkills.filter(tempSkill => {
        const originalSkill = skills.find(s => s.id === tempSkill.id);
        return originalSkill && originalSkill.years_of_experience !== tempSkill.years_of_experience;
      });

      // Delete skills
      for (const skill of skillsToDelete) {
        const { error } = await supabase
          .from("skills")
          .delete()
          .eq("id", skill.id);

        if (error) throw error;
      }

      // Add new skills
      for (const skill of skillsToAdd) {
        const { error } = await supabase
          .from("skills")
          .insert({
            skill_name: skill.skill_name,
            years_of_experience: skill.years_of_experience,
            category_id: categoryId,
            user_id: user.id
          });

        if (error) throw error;
      }

      // Update existing skills
      for (const skill of skillsToUpdate) {
        const { error } = await supabase
          .from("skills")
          .update({
            years_of_experience: skill.years_of_experience
          })
          .eq("id", skill.id)
          .eq("user_id", user.id);

        if (error) throw error;
      }

      queryClient.invalidateQueries({ queryKey: ["skills"] });
      setIsEditing(false);
      setTempSkills([]);

      toast({
        title: "Success",
        description: "Technical skills updated successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update technical skills",
        variant: "destructive",
      });
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setTempSkills([]);
    setNewSkill("");
    setNewYears("");
  };

  const handleAddSkill = () => {
    if (!newSkill || !newYears) return;

    const skillExists = tempSkills.some(
      skill => skill.skill_name.toLowerCase() === newSkill.toLowerCase()
    );

    if (skillExists) {
      toast({
        title: "Duplicate Skill",
        description: "This skill has already been added",
        variant: "destructive",
      });
      return;
    }

    setTempSkills([...tempSkills, {
      skill_name: newSkill,
      years_of_experience: parseInt(newYears),
      category_id: categories[0]?.id || "",
      isNew: true
    }]);

    setNewSkill("");
    setNewYears("");
    setShowSuggestions(false);
  };

  const handleRemoveSkill = (skillToRemove: TempSkill) => {
    setTempSkills(tempSkills.filter(skill =>
      skill.id ? skill.id !== skillToRemove.id : skill.skill_name !== skillToRemove.skill_name
    ));
  };

  const handleUpdateYears = async (skill: Skill | TempSkill, years: number) => {
    if (isEditing) {
      // Update in temporary state
      setTempSkills(tempSkills.map(tempSkill =>
        (tempSkill.id === skill.id || tempSkill.skill_name === skill.skill_name)
          ? { ...tempSkill, years_of_experience: years }
          : tempSkill
      ));
    } else {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error("No user found");

        const { error } = await supabase
          .from("skills")
          .update({ years_of_experience: years })
          .eq("id", skill.id)
          .eq("user_id", user.id);

        if (error) throw error;

        queryClient.invalidateQueries({ queryKey: ["skills"] });

        toast({
          title: "Success",
          description: "Years of experience updated successfully",
        });
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to update years of experience",
          variant: "destructive",
        });
      }
    }
  };

  if (isCategoriesLoading || isSkillsLoading || isTermsLoading) {
    return (
      <Card className="p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Technical skills</h2>
        </div>
        <div className="flex flex-wrap gap-2">
          <Skeleton className="h-6 w-24" />
          <Skeleton className="h-6 w-32" />
          <Skeleton className="h-6 w-28" />
          <Skeleton className="h-6 w-20" />
          <Skeleton className="h-6 w-36" />
        </div>
      </Card>
    );
  }

  if (isEditing) {
    return (
      <Card className="p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Technical skills</h2>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Input
              ref={inputRef}
              placeholder="Skill name"
              value={newSkill}
              onChange={(e) => {
                setNewSkill(e.target.value);
                setShowSuggestions(true);
              }}
              onFocus={() => setShowSuggestions(true)}
              className="w-[200px]"
            />
            {showSuggestions && (
              <div ref={dropdownRef} className="absolute w-full z-10">
                <Command className="rounded-lg border shadow-md">
                  <CommandList>
                    <CommandEmpty>No skill found.</CommandEmpty>
                    <CommandGroup className="max-h-[200px] overflow-auto">
                      {skillTerms
                        .filter(term =>
                          term.term.toLowerCase().includes(newSkill.toLowerCase())
                        )
                        .map((term) => (
                          <CommandItem
                            key={term.id}
                            value={term.term}
                            onSelect={(value) => {
                              setNewSkill(value);
                              setShowSuggestions(false);
                            }}
                            className="cursor-pointer"
                          >
                            {term.term}
                          </CommandItem>
                        ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </div>
            )}
          </div>
          <Input
            placeholder="Years"
            type="number"
            value={newYears}
            onChange={(e) => setNewYears(e.target.value)}
            className="w-20"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={handleAddSkill}
            className="whitespace-nowrap"
          >
            + Add
          </Button>
        </div>
        <div className="flex flex-wrap gap-2 my-4">
          {tempSkills.map((skill) => (
            <div key={skill.id || skill.skill_name} className="flex items-center">
              <YearsExperiencePopover
                skillName={skill.skill_name}
                currentYears={skill.years_of_experience}
                onSave={(years) => handleUpdateYears(skill, years)}
                className="pr-2 py-2"
                endContent={
                  <button
                    style={{ fontSize: "11px" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveSkill(skill);
                    }}
                    className="mx-1 hover:text-destructive"
                  >
                    X
                  </button>
                }
              />
            </div>
          ))}
        </div>

        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button onClick={handleSave}>
            Save
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Technical skills</h2>
        <Button
          variant="ghost"
          size="icon"
          onClick={handleStartEditing}
        >
          <PencilIcon className="h-4 w-4" />
        </Button>
      </div>
      <div className="flex flex-wrap gap-2">
        {skills.map((skill) => (
          <YearsExperiencePopover
            key={skill.id}
            skillName={skill.skill_name}
            currentYears={skill.years_of_experience}
            onSave={(years) => handleUpdateYears(skill, years)}
          />
        ))}
      </div>
    </Card>
  );
};

export default TechnicalSkills;